import React from 'react';
import ReactMarkdown, { type Options } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { disableIndentedCode } from './plugins/disableIndentedCode';

const LinkRenderer = (props: React.ComponentPropsWithoutRef<'a'>) => {
  return (
    <a
      className={'font-semibold text-ebony_opacity_80 underline cursor-pointer hover:text-ebony'}
      href={props.href}
      target="_blank"
      rel="noreferrer"
    >
      {props.children}
    </a>
  );
};

const ULRenderer = (props: React.ComponentPropsWithoutRef<'ul'>) => {
  return <ul className={'list-disc pl-6 mb-2.5'}>{props.children}</ul>;
};

const OLRenderer = (props: React.ComponentPropsWithoutRef<'ol'>) => {
  return <ol className={'list-decimal pl-6 mb-2.5'}>{props.children}</ol>;
};

const LiRenderer = (props: React.ComponentPropsWithoutRef<'li'>) => {
  return <li className={'[&_p]:mb-0'}>{props.children}</li>;
};

const DivRenderer = (props: React.ComponentPropsWithoutRef<'div'>) => {
  return <div className={'mb-[15px]'}>{props.children}</div>;
};

const PRenderer = (props: React.ComponentPropsWithoutRef<'p'>) => {
  return <p className={'break-words mb-[15px]'}>{props.children}</p>;
};

const H2Renderer = (props: React.ComponentPropsWithoutRef<'h2'>) => {
  return <h2 className={'break-words mb-[15px]'}>{props.children}</h2>;
};

const PreRenderer = ({ children }: React.ComponentPropsWithoutRef<'pre'>) => {
  return (
    <pre
      className={
        'bg-[#f4f4f4] border border-[#ddd] border-l-2 border-l-purple text-[#666] text-xs whitespace-normal max-w-full overflow-auto py-2 px-2 break-words [&_code]:bg-transparent [&_code]:border-0'
      }
    >
      {children}
    </pre>
  );
};

const CodeRenderer = ({ children }: React.ComponentPropsWithoutRef<'code'>) => {
  return (
    <code className={'break-all px-1 py-[1px] rounded-sm-5 bg-[#CAC3FF] border leading-[160%]'}>
      {children}
    </code>
  );
};
const ImgRenderer = ({ alt = '', src }: React.ComponentPropsWithoutRef<'img'>) => {
  return <img alt={alt} src={src} className={'max-w-[50%] mx-auto'} />;
};

const HRRenderer = () => {
  return <hr className={'border-0'} />;
};

const ScriptRenderer = ({ children }: React.ComponentPropsWithoutRef<'script'>) => {
  return <>{`<script>${children}</script>`}</>;
};

// The markdown source goes in `children`; other react-markdown options pass through.
export const MarkdownRenderer = ({ children, ...props }: Options) => {
  return (
    <ReactMarkdown
      components={{
        h2: H2Renderer,
        p: PRenderer,
        a: LinkRenderer,
        ul: ULRenderer,
        ol: OLRenderer,
        li: LiRenderer,
        pre: PreRenderer,
        code: CodeRenderer,
        hr: HRRenderer,
        div: DivRenderer,
        img: ImgRenderer,
        script: ScriptRenderer,
      }}
      {...props}
      rehypePlugins={[rehypeRaw, disableIndentedCode]}
    >
      {children}
    </ReactMarkdown>
  );
};
